import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { FaTimes } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

// Original category config for store metadata
const categoryConfig = {
  Console: {
    conditions: ["Used", "New"],
    options: ["PS5", "PS4", "Nintendo Switch", "Nintendo Switch 2", "Xbox Series", "Lenovo Legion Go", "Lenovo Legion Go 2", "ROG Ally", "Steam Deck", "Steam Deck OLED"],
  },
  "Game CDs": {
    conditions: ["Used", "New"],
    options: ["PS5", "PS4", "Xbox Series X", "Nintendo Switch", "Nintendo Switch 2"],
  },
  "Phone & Laptop": { conditions: ["Used", "New"], options: [] },
  Controller: { conditions: ["Used", "New"], options: [] },
  Games: { conditions: ["Used", "New"], options: [] },
  Accessories: { conditions: ["Used", "New"], options: [] },
  "Gift Cards": { conditions: ["Used", "New"], options: [] },
  "VR Headsets": { conditions: ["Used", "New"], options: [] },
};

// Distinct mapping for the typeId (Navbar Routing)
const navbarRoutes = [
  { group: "Consoles", items: [{ id: "ps5", label: "PS5" }, { id: "ps4", label: "PS4" }, { id: "switch", label: "Nintendo Switch" }, { id: "xbox", label: "Xbox Series" }, { id: "handhelds", label: "Steam Deck & Handhelds" }] },
  { group: "Game CDs", items: [{ id: "games-ps5", label: "PS5 Games" }, { id: "games-ps4", label: "PS4 Games" }, { id: "games-xbox", label: "Xbox Games" }, { id: "digital", label: "All Games" }] },
  { group: "Accessories", items: [{ id: "controllers", label: "Controllers" }, { id: "vr", label: "VR Headsets" }, { id: "accessories", label: "General Accessories" }] },
  { group: "Electronics", items: [{ id: "tech", label: "Phone & Laptop" }] },
];

const UpdateProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = Cookies.get("gameport");
  const queryClient = useQueryClient();
  const API_URL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    condition: "",
    platform: "",
    typeId: "", // Maps to the Navbar ID
    brand: "",
    quantity: "",
    tags: "",
    discount: 0,
    status: "active",
  });

  const [existingImgs, setExistingImgs] = useState([]);
  const [newImgs, setNewImgs] = useState([]);
  const [newPreview, setNewPreview] = useState([]);
  const [errors, setErrors] = useState({});

  const inputBase =
    "w-full mt-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-gray-800 " +
    "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600";

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["product", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products/${id}`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.message || "Failed to fetch product");
      }

      return json;
    },
  });

  const product = useMemo(() => data?.product || data, [data]);

  useEffect(() => {
    if (!product?._id) return;

    setFormData({
      name: product?.name || "",
      description: product?.description || "",
      price: product?.price ?? "",
      category: product?.category || "",
      condition: product?.condition || "",
      platform: product?.platform || "",
      typeId: product?.typeId || "",
      brand: product?.brand || "",
      quantity: product?.quantity ?? "",
      tags: Array.isArray(product?.tags)
        ? product.tags.join(", ")
        : product?.tags || "",
      discount: product?.discount ?? 0,
      status: product?.status || "active",
    });

    setExistingImgs(Array.isArray(product?.images) ? product.images : []);
  }, [product]);

  const selectedCategoryData = useMemo(() => {
    return categoryConfig[formData.category] || { conditions: [], options: [] };
  }, [formData.category]);

  const finalPrice = useMemo(() => {
    const price = Number(formData.price || 0);
    const discount = Number(formData.discount || 0);

    if (!price) return "0.00";
    if (!discount) return price.toFixed(2);

    const calculated = price - (price * discount) / 100;
    return calculated.toFixed(2);
  }, [formData.price, formData.discount]);

  useEffect(() => {
    return () => {
      newPreview.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [newPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "category") {
        return {
          ...prev,
          category: value,
          condition: "",
          platform: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleNewImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const previews = files.map((file) => URL.createObjectURL(file));

    setNewImgs((prev) => [...prev, ...files]);
    setNewPreview((prev) => [...prev, ...previews]);

    setErrors((prev) => ({
      ...prev,
      images: undefined,
    }));
  };

  const removeExistingImage = (url) => {
    setExistingImgs((prev) => prev.filter((img) => img !== url));
  };

  const removeNewImage = (index) => {
    setNewImgs((prev) => prev.filter((_, i) => i !== index));

    setNewPreview((prev) => {
      const removed = prev[index];
      if (removed?.startsWith("blob:")) {
        URL.revokeObjectURL(removed);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const validate = () => {
    const e = {};

    if (!formData.name.trim()) e.name = "Product name required";
    if (!formData.description.trim()) e.description = "Description required";
    if (!formData.price || Number(formData.price) <= 0) e.price = "Valid price required";
    if (!formData.category) e.category = "Category required";
    if (!formData.typeId) e.typeId = "Navbar route mapping required";
    if (!formData.brand.trim()) e.brand = "Brand required";
    if (!formData.quantity || Number(formData.quantity) < 0) {
      e.quantity = "Valid quantity required";
    }
    if (Number(formData.discount) < 0 || Number(formData.discount) > 100) {
      e.discount = "Discount must be between 0 and 100";
    }

    if (selectedCategoryData.conditions.length > 0 && !formData.condition) {
      e.condition = "Condition required";
    }

    if (selectedCategoryData.options.length > 0 && !formData.platform) {
      e.platform = "Option required";
    }

    if (existingImgs.length + newImgs.length === 0) {
      e.images = "Product must have at least one image";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const {
    mutate: updateProduct,
    isPending: updating,
    isError: updateIsError,
    error: updateError,
  } = useMutation({
    mutationKey: ["update-product", id],
    mutationFn: async () => {
      if (!token) {
        throw new Error("You are not authenticated.");
      }

      const form = new FormData();

      form.append("name", formData.name.trim());
      form.append("description", formData.description.trim());
      form.append("price", String(formData.price));
      form.append("discount", String(formData.discount || 0));
      form.append("category", formData.category);
      form.append("condition", formData.condition);
      form.append("platform", formData.platform);
      form.append("typeId", formData.typeId); // Submits Navbar ID
      form.append("brand", formData.brand.trim());
      form.append("quantity", String(formData.quantity));
      form.append("status", formData.status);

      const tagArr =
        typeof formData.tags === "string"
          ? formData.tags
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean)
          : [];

      form.append("tags", JSON.stringify(tagArr));
      form.append("existingImages", JSON.stringify(existingImgs));

      newImgs.forEach((img) => {
        form.append("images", img);
      });

      const res = await fetch(`${API_URL}/admin/update/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.message || "Failed to update product");
      }

      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["all-products"] });
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      navigate("/");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    updateProduct();
  };

  if (isPending) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-10">
            <p className="text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-10">
            <p className="text-sm text-indigo-600">{error?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-10">
          <div className="text-center">
            <h2 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
              Update Product
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Edit product details and images
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-8 grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2"
          >
            {/* Name */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Product Name
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={inputBase}
                placeholder="Enter product name"
              />
              {errors.name && <p className="mt-1 text-xs text-indigo-600">{errors.name}</p>}
            </div>

            {/* Brand */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Brand</label>
              <input
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className={inputBase}
                placeholder="e.g. Sony, Microsoft"
              />
              {errors.brand && <p className="mt-1 text-xs text-indigo-600">{errors.brand}</p>}
            </div>

            {/* Price */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Price</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={inputBase}
                placeholder="Enter price"
              />
              {errors.price && <p className="mt-1 text-xs text-indigo-600">{errors.price}</p>}
            </div>

            {/* Discount */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Discount (%)
              </label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
                className={inputBase}
                placeholder="Enter discount"
              />
              {errors.discount && (
                <p className="mt-1 text-xs text-indigo-600">{errors.discount}</p>
              )}
            </div>

            {/* Final Price */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Final Price
              </label>
              <input
                value={`₦${Number(finalPrice).toLocaleString()}`}
                readOnly
                className="mt-1 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">
                Automatically calculated from price and discount
              </p>
            </div>

            {/* Navbar Route (Type ID) Dropdown */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Navbar Route Link (Type ID)
              </label>
              <select
                name="typeId"
                value={formData.typeId}
                onChange={handleChange}
                className={inputBase}
              >
                <option value="">Select where this appears in navbar</option>
                {navbarRoutes.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {errors.typeId && (
                <p className="mt-1 text-xs text-indigo-600">{errors.typeId}</p>
              )}
            </div>

            {/* Store Category */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Store Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={inputBase}
              >
                <option value="">Select category</option>
                {Object.keys(categoryConfig).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-xs text-indigo-600">{errors.category}</p>
              )}
            </div>

            {/* Condition */}
            {selectedCategoryData.conditions.length > 0 && (
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Condition
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className={inputBase}
                >
                  <option value="">Select condition</option>
                  {selectedCategoryData.conditions.map((condition) => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
                {errors.condition && (
                  <p className="mt-1 text-xs text-indigo-600">{errors.condition}</p>
                )}
              </div>
            )}

            {/* Platform / Model */}
            {selectedCategoryData.options.length > 0 && (
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  {formData.category === "Game CDs" ? "CD Platform" : "Console Type"}
                </label>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  className={inputBase}
                >
                  <option value="">Select option</option>
                  {selectedCategoryData.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.platform && (
                  <p className="mt-1 text-xs text-indigo-600">{errors.platform}</p>
                )}
              </div>
            )}

            {/* Status */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={inputBase}
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={inputBase}
                placeholder="Enter quantity"
              />
              {errors.quantity && (
                <p className="mt-1 text-xs text-indigo-600">{errors.quantity}</p>
              )}
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">
                Tags (comma separated)
              </label>
              <input
                name="tags"
                value={formData.tags}
                placeholder="gaming, console, ps5"
                onChange={handleChange}
                className={inputBase}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">
                Description
              </label>
              <textarea
                rows={5}
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={inputBase}
                placeholder="Enter product description"
              />
              {errors.description && (
                <p className="mt-1 text-xs text-indigo-600">{errors.description}</p>
              )}
            </div>

            {/* Add New Images */}
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Add New Images
              </label>

              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleNewImages}
                  className="block w-full text-sm text-gray-700"
                />
                <p className="mt-2 text-xs text-gray-500">
                  You can keep old images, remove some, and add new ones.
                </p>
                {errors.images && <p className="mt-1 text-xs text-indigo-600">{errors.images}</p>}
              </div>
            </div>

            {/* Existing Images */}
            {existingImgs.length > 0 && (
              <div className="md:col-span-2">
                <p className="mb-2 text-sm font-semibold text-gray-700">
                  Existing Images
                </p>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {existingImgs.map((url, i) => (
                    <div
                      key={`${url}-${i}`}
                      className="group relative overflow-hidden rounded-xl border border-gray-100"
                    >
                      <img
                        src={url}
                        alt=""
                        className="h-32 w-full object-cover sm:h-36"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(url)}
                        className="absolute right-2 top-2 rounded-full bg-indigo-600 p-2 text-white opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100"
                        aria-label="Remove existing image"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Image Previews */}
            {newPreview.length > 0 && (
              <div className="md:col-span-2">
                <p className="mb-2 text-sm font-semibold text-gray-700">
                  New Images
                </p>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {newPreview.map((img, i) => (
                    <div
                      key={`${img}-${i}`}
                      className="group relative overflow-hidden rounded-xl border border-gray-100"
                    >
                      <img
                        src={img}
                        alt=""
                        className="h-32 w-full object-cover sm:h-36"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(i)}
                        className="absolute right-2 top-2 rounded-full bg-indigo-600 p-2 text-white opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100"
                        aria-label="Remove new image"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Update Error */}
            {updateIsError && (
              <p className="text-sm text-indigo-600 md:col-span-2">
                {updateError?.message}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={updating}
              className={`rounded-xl py-3 font-semibold transition md:col-span-2 ${
                updating
                  ? "cursor-not-allowed bg-gray-300 text-gray-700"
                  : "bg-indigo-600 text-white hover:opacity-90"
              }`}
            >
              {updating ? "Updating..." : "Update Product"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateProduct;